import React, { useEffect, useMemo, useState } from 'react';
import { getCounts, getTimeSeriesBookings, getUpcomingOccupancy, getTopEvents, getRoleCounts } from '../lib/analytics';
import Sparkline from './admin/charts/Sparkline';
import BarList from './admin/charts/BarList';

export default function AdminDashboard({ days = 30, organizerId, movieId }: { days?: number; organizerId?: number | null; movieId?: number | null }) {
  const [stats, setStats] = useState<{ events: number; organizers: number; bookings: number; users: number; editors: number }>({ events: 0, organizers: 0, bookings: 0, users: 0, editors: 0 });
  const [roles, setRoles] = useState<Record<string, number>>({})
  const [bookingsSeries, setBookingsSeries] = useState<Array<{ date: string; count: number; revenue: number }>>([])
  const [occupancy, setOccupancy] = useState<Array<{ id: number; movie_id: number; start_time: string; location: string; capacity: number; booked: number }>>([])
  const [topEvents, setTopEvents] = useState<Array<{ movie_id: number; title: string; tickets: number }>>([])

  useEffect(() => {
    (async () => {
      try {
        const [c, r, s, o, t] = await Promise.all([
          getCounts(organizerId ? { organizerId } : undefined),
          getRoleCounts(),
          getTimeSeriesBookings(days, organizerId || movieId ? { organizerId: organizerId || undefined, movieId: movieId || undefined } : undefined),
          getUpcomingOccupancy(6, organizerId || movieId ? { organizerId: organizerId || undefined, movieId: movieId || undefined } : undefined),
          getTopEvents(5, organizerId ? { organizerId } : undefined),
        ])
        setStats(c)
        setRoles(r)
        setBookingsSeries(s)
        setOccupancy(o)
        setTopEvents(t)
      } catch {}
    })()
  }, [days, organizerId, movieId]);

  const bookingsSpark = useMemo(() => bookingsSeries.map(b => b.count), [bookingsSeries])
  const revenueSpark = useMemo(() => bookingsSeries.map(b => b.revenue || 0), [bookingsSeries])

  return (
    <div id="dashboard" className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="flex flex-col gap-2 rounded-xl p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-700 dark:text-gray-300">Events</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.events}</p>
        </div>
        <div className="flex flex-col gap-2 rounded-xl p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-700 dark:text-gray-300">Organizers</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.organizers}</p>
        </div>
        <div className="flex flex-col gap-2 rounded-xl p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-700 dark:text-gray-300">Bookings</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.bookings}</p>
        </div>
        <div className="flex flex-col gap-2 rounded-xl p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-700 dark:text-gray-300">Users</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.users}</p>
        </div>
        <div className="flex flex-col gap-2 rounded-xl p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-700 dark:text-gray-300">Editors</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.editors}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Bookings ({days}d)</h3>
          </div>
          <div className="mt-4">
            <Sparkline values={bookingsSpark} color="#4f46e5" />
          </div>
        </div>
        {revenueSpark.some(v => v > 0) && (
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Revenue ({days}d)</h3>
            </div>
            <div className="mt-4">
              <Sparkline values={revenueSpark} color="#f59e0b" />
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Upcoming Screenings</h3>
          <div className="mt-4 space-y-3">
            {occupancy.length === 0 ? (
              <p className="text-sm text-gray-700 dark:text-gray-300">No upcoming screenings</p>
            ) : occupancy.map(s => {
              const pct = s.capacity > 0 ? Math.round((s.booked / s.capacity) * 100) : 0
              return (
                <div key={s.id} className="flex items-center gap-3">
                  <div className="flex-1 h-2 rounded bg-gray-200 dark:bg-gray-700">
                    <div className={`h-2 rounded ${pct >= 90 ? 'bg-red-500' : pct >= 60 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{ width: `${Math.min(100, pct)}%` }} />
                  </div>
                  <div className="min-w-[180px] text-sm text-gray-900 dark:text-white">{new Date(s.start_time).toLocaleString()}</div>
                  <div className="w-20 text-right text-sm text-gray-700 dark:text-gray-300">{pct}%</div>
                </div>
              )
            })}
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Top Events</h3>
          <div className="mt-4">
            <BarList items={topEvents.map(e => ({ label: e.title, value: e.tickets }))} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="flex flex-col gap-2 rounded-xl p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-700 dark:text-gray-300">Organizers</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{roles.organizer || 0}</p>
        </div>
        <div className="flex flex-col gap-2 rounded-xl p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-700 dark:text-gray-300">Validators</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{roles.validator || 0}</p>
        </div>
        <div className="flex flex-col gap-2 rounded-xl p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-700 dark:text-gray-300">Editors</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{roles.editor || 0}</p>
        </div>
        <div className="flex flex-col gap-2 rounded-xl p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-700 dark:text-gray-300">Admins</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{roles.admin || 0}</p>
        </div>
      </div>
    </div>
  )
}
